/**
 * Talab has ONE global customer identity. Each merchant then has its own,
 * isolated relationship with that person — so the same Ahmed can be a customer
 * of EasyLease and of Adidas without either merchant ever seeing the other's
 * orders, leads or conversations.
 *
 * Merchant-facing UI always says "Customers". The relationship below is what
 * the merchant actually sees; the global identity is never exposed.
 */

/** The global Talab account. Shared, and never shown to a merchant. */
export interface TalabIdentity {
  id: string
  name: string
  phone: string
  email?: string
  avatar?: string
}

export type CustomerSource =
  | 'My Shop'
  | 'Website Chat'
  | 'Talab Discover'
  | 'Added manually'

/** One merchant's relationship with one Talab identity. */
export interface MerchantCustomer {
  /** Relationship id, scoped to the merchant. */
  id: string
  merchantId: string
  identityId: string
  name: string
  phone: string
  email?: string
  source: CustomerSource
  firstSeen: string
  lastActivity: string
  /** Sort key — days since the last activity. */
  lastActivityDays: number
  orders: number
  spend: number
  leads: number
  conversations: number
  marketingConsent: boolean
}

export const IDENTITIES: TalabIdentity[] = [
  {
    id: 'usr_123',
    name: 'Ahmed Hassan',
    phone: '+971 50 441 9022',
    email: 'ahmed.hassan@gmail.com',
  },
  {
    id: 'usr_128',
    name: 'Sarah Ali',
    phone: '+971 55 210 4471',
    email: 'sarah.ali@email.com',
  },
]

/**
 * Every relationship for every merchant lives in one list, keyed by merchant —
 * exactly how the real thing is isolated. A page only ever reads its own
 * merchant's slice.
 */
const RELATIONSHIPS: MerchantCustomer[] = [
  /* ---- EasyLease ------------------------------------------------------- */
  {
    id: 'el_1',
    merchantId: 'easylease',
    identityId: 'usr_123',
    name: 'Ahmed Hassan',
    phone: '+971 50 441 9022',
    email: 'ahmed.hassan@gmail.com',
    source: 'My Shop',
    firstSeen: '13 Sep 2026',
    lastActivity: 'Today',
    lastActivityDays: 0,
    orders: 2,
    spend: 8900,
    leads: 1,
    conversations: 1,
    marketingConsent: true,
  },
  {
    id: 'el_2',
    merchantId: 'easylease',
    identityId: 'usr_128',
    name: 'Sarah Ali',
    phone: '+971 55 210 4471',
    email: 'sarah.ali@email.com',
    source: 'Website Chat',
    firstSeen: '12 Sep 2026',
    lastActivity: 'Yesterday',
    lastActivityDays: 1,
    orders: 0,
    spend: 0,
    leads: 2,
    conversations: 1,
    marketingConsent: false,
  },
  {
    id: 'el_3',
    merchantId: 'easylease',
    identityId: 'usr_140',
    name: 'Omar Khan',
    phone: '+971 55 776 2210',
    email: 'omar.khan@corpmail.ae',
    source: 'Talab Discover',
    firstSeen: '2 Aug 2026',
    lastActivity: '3 days ago',
    lastActivityDays: 3,
    orders: 4,
    spend: 21400,
    leads: 3,
    conversations: 2,
    marketingConsent: true,
  },
  {
    id: 'el_4',
    merchantId: 'easylease',
    identityId: 'usr_141',
    name: 'Fatima Al Mansoori',
    phone: '+971 50 118 4402',
    source: 'My Shop',
    firstSeen: '11 Sep 2026',
    lastActivity: 'Today',
    lastActivityDays: 0,
    orders: 0,
    spend: 0,
    leads: 0,
    conversations: 0,
    marketingConsent: false,
  },
  {
    id: 'el_5',
    merchantId: 'easylease',
    identityId: 'usr_142',
    name: 'Marina Heights Café',
    phone: '+971 4 556 7120',
    email: 'ops@marinaheights.ae',
    source: 'Website Chat',
    firstSeen: '20 Jul 2026',
    lastActivity: '6 days ago',
    lastActivityDays: 6,
    orders: 3,
    spend: 9860,
    leads: 1,
    conversations: 3,
    marketingConsent: true,
  },
  {
    id: 'el_6',
    merchantId: 'easylease',
    identityId: 'usr_143',
    name: 'Layla Hassan',
    phone: '+971 52 665 1108',
    email: 'layla.h@corpmail.ae',
    source: 'My Shop',
    firstSeen: '9 Sep 2026',
    lastActivity: 'Today',
    lastActivityDays: 0,
    orders: 1,
    spend: 3620,
    leads: 0,
    conversations: 1,
    marketingConsent: false,
  },
  {
    id: 'el_7',
    merchantId: 'easylease',
    identityId: 'usr_144',
    name: 'Rami Doukhi',
    phone: '+971 56 330 8817',
    source: 'Added manually',
    firstSeen: '4 Jun 2026',
    lastActivity: '18 days ago',
    lastActivityDays: 18,
    orders: 0,
    spend: 0,
    leads: 1,
    conversations: 0,
    marketingConsent: false,
  },
  /* ---- Adidas — the same Ahmed, a completely separate relationship ------ */
  {
    id: 'ad_1',
    merchantId: 'adidas',
    identityId: 'usr_123',
    name: 'Ahmed Hassan',
    phone: '+971 50 441 9022',
    email: 'ahmed.hassan@gmail.com',
    source: 'My Shop',
    firstSeen: '13 Sep 2026',
    lastActivity: 'Today',
    lastActivityDays: 0,
    orders: 0,
    spend: 0,
    leads: 0,
    conversations: 0,
    marketingConsent: false,
  },
]

/** A merchant only ever reads its own slice. */
export function customersOf(merchantId: string): MerchantCustomer[] {
  return RELATIONSHIPS.filter((c) => c.merchantId === merchantId)
}

/** Plain-language tags, derived rather than managed by hand. */
export function customerTags(c: MerchantCustomer): string[] {
  const tags: string[] = []
  if (c.orders >= 2) tags.push('Repeat customer')
  else if (c.orders === 1) tags.push('Has ordered')
  if (c.lastActivityDays <= 2 && c.orders === 0) tags.push('New customer')
  if (c.source === 'My Shop') tags.push('From My Shop')
  if (c.marketingConsent) tags.push('Happy to be contacted')
  return tags
}

export function initials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map((p) => p.charAt(0))
    .join('')
    .toUpperCase()
}
